import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Package, Plus, RefreshCw, Trash2, Check, Search, Pencil } from "lucide-react";
import { Button } from "../components/ui/button";
import CustomerCombobox from "../components/CustomerCombobox";

// Machines bought but not yet on the farm — still at the supplier in China, or
// in transit. Deliberately kept out of the customer's machine list until they
// physically land, because that list is what hosting fees are computed from and
// nobody should pay to host a machine sitting in a warehouse.
const STATUSES = ["pending", "shipped", "arrived"];
const STATUS_STYLE = {
  pending: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
  shipped: "bg-cyan-500/10 text-cyan-400 border-cyan-500/30",
  arrived: "bg-[#00E054]/10 text-[#00E054] border-[#00E054]/30",
};

const PendingMachines = () => {
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [showArrived, setShowArrived] = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const blank = {
    customer_id: "", customer_name: "", machine_type_id: "", machine_name: "",
    quantity: 1, supplier: "", ordered_date: today, expected_date: "",
    tracking: "", status: "pending", notes: "",
  };
  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);

  const closeForm = () => { setShowForm(false); setForm(blank); setEditingId(null); };

  const startEdit = (row) => {
    setForm({ ...blank, ...row, quantity: row.quantity || 1 });
    setEditingId(row.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [p, t, c] = await Promise.all([
        axios.get(`${API}/pending-machines`, { params: { include_arrived: showArrived } }),
        axios.get(`${API}/machine-types`),
        axios.get(`${API}/customers`),
      ]);
      setRows(p.data || []);
      setTypes(t.data || []);
      setCustomers(c.data || []);
    } catch {
      toast.error("Could not load pending machines");
    }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [showArrived]);

  const save = async () => {
    if (!form.machine_name && !form.machine_type_id) {
      toast.error("Pick a machine type or type a name");
      return;
    }
    setSaving(true);
    try {
      const body = { ...form, quantity: Number(form.quantity) || 1 };
      if (editingId) {
        await axios.put(`${API}/pending-machines/${editingId}`, body);
        toast.success("Saved");
      } else {
        await axios.post(`${API}/pending-machines`, body);
        toast.success("Added");
      }
      closeForm();
      fetchAll();
    } catch {
      toast.error("Could not save");
    }
    setSaving(false);
  };

  const remove = async (row) => {
    if (!window.confirm(`Remove ${row.quantity} × ${row.machine_name || "machine"} from the pending list?`)) return;
    try {
      await axios.delete(`${API}/pending-machines/${row.id}`);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      toast.success("Removed");
    } catch {
      toast.error("Could not remove");
    }
  };

  // The one action that touches money. Spelled out in the confirmation rather
  // than left as a guess, because it starts the customer's hosting fee.
  const markArrived = async (row) => {
    const who = row.customer_name ? ` and add it to ${row.customer_name}` : "";
    const fee = row.customer_id ? " Their hosting fee will go up from now." : "";
    if (!window.confirm(`Mark ${row.quantity} × ${row.machine_name || "machine"} as arrived${who}?${fee}`)) return;
    try {
      const r = await axios.post(`${API}/pending-machines/${row.id}/arrived`);
      toast.success(r.data.added_to_customer
        ? `Arrived — added to ${r.data.added_to_customer}`
        : "Marked arrived");
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark arrived");
    }
  };

  const filtered = query.trim()
    ? rows.filter((r) => {
        const q = query.toLowerCase();
        return (r.customer_name || "").toLowerCase().includes(q)
          || (r.machine_name || "").toLowerCase().includes(q)
          || (r.supplier || "").toLowerCase().includes(q)
          || (r.tracking || "").toLowerCase().includes(q);
      })
    : rows;

  const unitsPending = filtered
    .filter((r) => r.status !== "arrived")
    .reduce((s, r) => s + (Number(r.quantity) || 0), 0);
  // "Overdue" only means the supplier's own estimate has passed. It is a nudge
  // to go and ask, not a fact about the shipment.
  const overdue = filtered.filter(
    (r) => r.status !== "arrived" && r.expected_date && r.expected_date < today
  ).length;

  const input = "w-full bg-[#0A0A0A] border border-[#27272A] rounded-xl px-3 py-2 text-sm text-white outline-none focus:border-[#00E054]";

  return (
    <div className="space-y-6" data-testid="pending-machines-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Package size={24} className="text-[#00E054]" /> Pending Machines
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Bought but not on the farm yet — still in China or in transit. Not billed until you mark them arrived.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-[#27272A] text-gray-300" onClick={fetchAll}
                  data-testid="btn-refresh-pending">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
          <Button className="bg-[#00E054] hover:bg-[#00E054]/80 text-black font-bold"
                  onClick={() => (showForm ? closeForm() : setShowForm(true))} data-testid="btn-new-pending">
            <Plus size={14} className="mr-1" /> Add
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-4">
          <p className="text-xs text-gray-500">Orders pending</p>
          <p className="text-2xl font-bold text-white mt-1">
            {filtered.filter((r) => r.status !== "arrived").length}
          </p>
        </div>
        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-4">
          <p className="text-xs text-gray-500">Machines on the way</p>
          <p className="text-2xl font-bold text-cyan-400 mt-1">{unitsPending}</p>
        </div>
        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-4">
          <p className="text-xs text-gray-500">Past expected date</p>
          <p className={`text-2xl font-bold mt-1 ${overdue ? "text-yellow-400" : "text-gray-600"}`}>{overdue}</p>
        </div>
      </div>

      {showForm && (
        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-4 space-y-3" data-testid="pending-form">
          {editingId && <p className="text-sm font-semibold text-white">Edit pending order</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500">Customer (leave blank if it's yours)</label>
              <CustomerCombobox
                value={form.customer_id}
                onChange={(id) => setForm({
                  ...form,
                  customer_id: id || "",
                  customer_name: (customers.find((c) => c.id === id)?.name) || "",
                })}
              />
            </div>
            <div>
              <label className="text-xs text-gray-500">Machine type</label>
              <select className={input} value={form.machine_type_id}
                      onChange={(e) => {
                        const t = types.find((x) => x.id === e.target.value);
                        setForm({ ...form, machine_type_id: e.target.value, machine_name: t?.name || form.machine_name });
                      }}>
                <option value="">— pick a type —</option>
                {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500">Quantity</label>
              <input type="number" min="1" className={input} value={form.quantity}
                     onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-gray-500">Supplier</label>
              <input className={input} value={form.supplier} placeholder="e.g. Bitmain, Shenzhen reseller"
                     onChange={(e) => setForm({ ...form, supplier: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-gray-500">Ordered</label>
              <input type="date" className={input} value={form.ordered_date}
                     onChange={(e) => setForm({ ...form, ordered_date: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-gray-500">Expected on the farm</label>
              <input type="date" className={input} value={form.expected_date}
                     onChange={(e) => setForm({ ...form, expected_date: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-gray-500">Tracking / container</label>
              <input className={input} value={form.tracking}
                     onChange={(e) => setForm({ ...form, tracking: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-gray-500">Status</label>
              <select className={input} value={form.status}
                      onChange={(e) => setForm({ ...form, status: e.target.value })}>
                {STATUSES.filter((s) => s !== "arrived").map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500">Notes</label>
            <input className={input} value={form.notes} placeholder="deposit paid, PSU included, …"
                   onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" className="border-[#27272A] text-gray-300"
                    onClick={closeForm}>Cancel</Button>
            <Button className="bg-[#00E054] hover:bg-[#00E054]/80 text-black font-bold"
                    disabled={saving} onClick={save} data-testid="btn-save-pending">
              {saving ? "Saving…" : editingId ? "Save changes" : "Add to pending"}
            </Button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-md flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input className={`${input} pl-9`} placeholder="Search customer, machine, supplier, tracking…"
                 value={query} onChange={(e) => setQuery(e.target.value)} data-testid="pending-search" />
        </div>
        <label className="text-sm text-gray-400 flex items-center gap-2">
          <input type="checkbox" checked={showArrived} onChange={(e) => setShowArrived(e.target.checked)} />
          Show already arrived
        </label>
      </div>

      <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl overflow-hidden">
        {loading ? (
          <p className="text-center py-10 text-gray-500">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-center py-10 text-gray-500">
            Nothing pending. Machines added here stay out of billing until you mark them arrived.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#111] text-gray-400">
                <tr>
                  <th className="text-left px-4 py-3">Customer</th>
                  <th className="text-left px-4 py-3">Machine</th>
                  <th className="text-left px-4 py-3">Qty</th>
                  <th className="text-left px-4 py-3">Supplier</th>
                  <th className="text-left px-4 py-3">Ordered</th>
                  <th className="text-left px-4 py-3">Expected</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const late = r.status !== "arrived" && r.expected_date && r.expected_date < today;
                  return (
                    <tr key={r.id} className="border-t border-[#27272A]" data-testid="pending-row">
                      <td className="px-4 py-3 text-white">{r.customer_name || <span className="text-gray-500">— mine —</span>}</td>
                      <td className="px-4 py-3 text-gray-300">
                        {r.machine_name || "—"}
                        {r.notes ? <span className="block text-xs text-gray-500">{r.notes}</span> : null}
                      </td>
                      <td className="px-4 py-3 text-gray-300">{r.quantity}</td>
                      <td className="px-4 py-3 text-gray-400">{r.supplier || "—"}</td>
                      <td className="px-4 py-3 text-gray-400">{r.ordered_date || "—"}</td>
                      <td className={`px-4 py-3 ${late ? "text-yellow-400" : "text-gray-400"}`}>
                        {r.expected_date || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded-lg border ${STATUS_STYLE[r.status] || ""}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2 justify-end">
                          {r.status !== "arrived" && (
                            <Button size="sm" variant="outline" className="border-[#27272A] text-gray-300"
                                    onClick={() => startEdit(r)} data-testid="btn-edit-pending">
                              <Pencil size={14} />
                            </Button>
                          )}
                          {r.status !== "arrived" && (
                            <Button size="sm" className="bg-[#00E054] hover:bg-[#00E054]/80 text-black font-bold"
                                    onClick={() => markArrived(r)} data-testid="btn-mark-arrived">
                              <Check size={14} className="mr-1" /> Arrived
                            </Button>
                          )}
                          <Button size="sm" variant="outline" className="border-[#27272A] text-gray-400"
                                  onClick={() => remove(r)} data-testid="btn-delete-pending">
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PendingMachines;
